import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import { Modal, Container, Row, Col, ButtonToolbar, Button, Form, Card, Accordion, Badge } from 'react-bootstrap';
import { mexicoCityNowTime, mexicoCityToday } from '../../../../utils/dateTime';

function DefaultModal(props) {
    const today = mexicoCityToday();
    const currentTime = mexicoCityNowTime();
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();

    const [maintenanceData, setMaintenanceData] = useState({
        inventory_id: '',
        maintenance_location: '',
        maintenance_type: 'preventive',
        date_entered: today,
        time_entered: currentTime,
        date_started: '',
        time_started: '',
        date_finished: '',
        time_finished: '',
        technician_name: '',
        diagnostics: '',
        results: '',
        additional_notes: '',
        is_completed: false,
        total_cost: 0
    });

    const [maintenanceItems, setMaintenanceItems] = useState([]);
    const [selectedChecks, setSelectedChecks] = useState({});
    const [loading, setLoading] = useState(false);

    // Fetch maintenance items grouped by areas when modal opens
    useEffect(() => {
        if (props.show && props.maintenanceAreas) {
            fetchMaintenanceItems();
        }
    }, [props.show, props.maintenanceAreas]);

    const fetchMaintenanceItems = async () => {
        try {
            const response = await axios.get(`${apiUrl}/maintenance/items`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setMaintenanceItems(response.data.msg.MaintenanceItems || []);
        } catch (error) {
            console.error('Error fetching maintenance items:', error);
            alert('Error al cargar elementos de mantenimiento');
        }
    };

    const handleMaintenanceDataChange = (field, value) => {
        setMaintenanceData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCheckChange = (itemId, field, value) => {
        setSelectedChecks(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                [field]: value,
                maintenance_item_id: itemId
            }
        }));
    };

    const toggleItemCheck = (itemId) => {
        setSelectedChecks(prev => {
            const newChecks = { ...prev };
            if (newChecks[itemId]) {
                delete newChecks[itemId];
            } else {
                newChecks[itemId] = {
                    maintenance_item_id: itemId,
                    status: 'pending',
                    cost: 0,
                    observations: '',
                    parts_replaced: '',
                    measurements: '',
                    before_condition: '',
                    after_condition: '',
                    checked_by: '',
                    date_checked: '',
                    time_checked: ''
                };
            }
            return newChecks;
        });
    };

    const calculateTotalCost = () => {
        const checksCost = Object.values(selectedChecks).reduce((total, check) => {
            return total + (parseFloat(check.cost) || 0);
        }, 0);
        return checksCost;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            // Prepare maintenance data
            const maintenancePayload = {
                ...maintenanceData,
                date_entered: `${maintenanceData.date_entered} ${maintenanceData.time_entered}`,
                date_started: maintenanceData.date_started ?
                    `${maintenanceData.date_started} ${maintenanceData.time_started || '00:00'}` : null,
                date_finished: maintenanceData.date_finished ?
                    `${maintenanceData.date_finished} ${maintenanceData.time_finished || '00:00'}` : null,
                total_cost: calculateTotalCost(),
                maintenance_checks: Object.values(selectedChecks).map(check => ({
                    ...check,
                    date_checked: check.date_checked ?
                        `${check.date_checked} ${check.time_checked || '00:00'}` : null
                }))
            };

            const response = await axios.post(`${apiUrl}/maintenance/maintenance`, maintenancePayload, {
                headers: {
                    Authorization: authHeader()
                }
            });

            props.setData();
            props.onHide();

            // Reset form
            setMaintenanceData({
                inventory_id: '',
                maintenance_location: '',
                maintenance_type: 'preventive',
                date_entered: today,
                time_entered: currentTime,
                date_started: '',
                time_started: '',
                date_finished: '',
                time_finished: '',
                technician_name: '',
                diagnostics: '',
                results: '',
                additional_notes: '',
                is_completed: false,
                total_cost: 0
            });
            setSelectedChecks({});

        } catch (error) {
            console.error('Error creating maintenance:', error);
            alert('Error al crear el registro de mantenimiento');
        } finally {
            setLoading(false);
        }
    };

    // Group items by area
    const itemsByArea = maintenanceItems.reduce((acc, item) => {
        const areaName = item.area_name || 'Sin Área';
        if (!acc[areaName]) {
            acc[areaName] = [];
        }
        acc[areaName].push(item);
        return acc;
    }, {});

    return (
        <Modal {...props} size="xl" aria-labelledby="contained-modal-title-vcenter">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Agregar Mantenimiento
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Container>
                    <Form onSubmit={handleSubmit}>
                        {/* Basic Maintenance Information */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información Básica del Mantenimiento</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Unidad *</Form.Label>
                                        <Form.Select
                                            value={maintenanceData.inventory_id}
                                            onChange={(e) => handleMaintenanceDataChange('inventory_id', e.target.value)}
                                            required
                                        >
                                            <option value="">Seleccionar unidad...</option>
                                            {props.inventoryOptions.map((unit) => (
                                                <option key={unit.id} value={unit.id}>
                                                    {unit.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Tipo de Mantenimiento *</Form.Label>
                                        <Form.Select
                                            value={maintenanceData.maintenance_type}
                                            onChange={(e) => handleMaintenanceDataChange('maintenance_type', e.target.value)}
                                            required
                                        >
                                            <option value="preventive">Preventivo</option>
                                            <option value="corrective">Correctivo</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Ubicación del Mantenimiento *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={maintenanceData.maintenance_location}
                                            onChange={(e) => handleMaintenanceDataChange('maintenance_location', e.target.value)}
                                            placeholder="Ej: Taller Central, Cliente, Carretera..."
                                            required
                                        />
                                    </Form.Group>
                                </Row>

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={3}>
                                        <Form.Label>Fecha de Ingreso *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={maintenanceData.date_entered}
                                            onChange={(e) => handleMaintenanceDataChange('date_entered', e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={3}>
                                        <Form.Label>Hora de Ingreso</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={maintenanceData.time_entered}
                                            onChange={(e) => handleMaintenanceDataChange('time_entered', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={3}>
                                        <Form.Label>Fecha de Inicio</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={maintenanceData.date_started}
                                            onChange={(e) => handleMaintenanceDataChange('date_started', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={3}>
                                        <Form.Label>Hora de Inicio</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={maintenanceData.time_started}
                                            onChange={(e) => handleMaintenanceDataChange('time_started', e.target.value)}
                                        />
                                    </Form.Group>
                                </Row>

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Técnico Responsable</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={maintenanceData.technician_name}
                                            onChange={(e) => handleMaintenanceDataChange('technician_name', e.target.value)}
                                            placeholder="Nombre del técnico"
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Check
                                            type="checkbox"
                                            label="Marcar como completado"
                                            checked={maintenanceData.is_completed}
                                            onChange={(e) => handleMaintenanceDataChange('is_completed', e.target.checked)}
                                            className="mt-4"
                                        />
                                    </Form.Group>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Maintenance Checks */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Verificaciones de Mantenimiento</h6>
                                <small className="text-muted">Selecciona los elementos a verificar durante el mantenimiento</small>
                            </Card.Header>
                            <Card.Body>
                                <Accordion>
                                    {Object.entries(itemsByArea).map(([areaName, items], areaIndex) => (
                                        <Accordion.Item eventKey={areaIndex.toString()} key={areaName}>
                                            <Accordion.Header>
                                                <div className="d-flex justify-content-between w-100 me-3">
                                                    <span>{areaName}</span>
                                                    <Badge bg="secondary">
                                                        {items.filter(item => selectedChecks[item.id]).length} / {items.length} seleccionados
                                                    </Badge>
                                                </div>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                {items.map(item => (
                                                    <Card key={item.id} className="mb-2">
                                                        <Card.Body className="py-2">
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    label={item.name}
                                                                    checked={!!selectedChecks[item.id]}
                                                                    onChange={() => toggleItemCheck(item.id)}
                                                                />
                                                                {item.description && (
                                                                    <small className="text-muted">{item.description}</small>
                                                                )}
                                                            </div>

                                                            {selectedChecks[item.id] && (
                                                                <div className="mt-3 pt-3 border-top">
                                                                    <Row>
                                                                        <Form.Group as={Col} md={3}>
                                                                            <Form.Label>Estado</Form.Label>
                                                                            <Form.Select
                                                                                size="sm"
                                                                                value={selectedChecks[item.id].status}
                                                                                onChange={(e) => handleCheckChange(item.id, 'status', e.target.value)}
                                                                            >
                                                                                <option value="pending">Pendiente</option>
                                                                                <option value="passed">Aprobado</option>
                                                                                <option value="failed">Fallido</option>
                                                                                <option value="needs_attention">Requiere Atención</option>
                                                                                <option value="not_applicable">No Aplica</option>
                                                                            </Form.Select>
                                                                        </Form.Group>
                                                                        <Form.Group as={Col} md={3}>
                                                                            <Form.Label>Costo</Form.Label>
                                                                            <Form.Control
                                                                                size="sm"
                                                                                type="number"
                                                                                step="0.01"
                                                                                placeholder="0.00"
                                                                                value={selectedChecks[item.id].cost}
                                                                                onChange={(e) => handleCheckChange(item.id, 'cost', e.target.value)}
                                                                            />
                                                                        </Form.Group>
                                                                        <Form.Group as={Col} md={6}>
                                                                            <Form.Label>Observaciones</Form.Label>
                                                                            <Form.Control
                                                                                size="sm"
                                                                                type="text"
                                                                                placeholder="Observaciones..."
                                                                                value={selectedChecks[item.id].observations}
                                                                                onChange={(e) => handleCheckChange(item.id, 'observations', e.target.value)}
                                                                            />
                                                                        </Form.Group>
                                                                    </Row>
                                                                </div>
                                                            )}
                                                        </Card.Body>
                                                    </Card>
                                                ))}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            </Card.Body>
                        </Card>

                        {/* Additional Information */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información Adicional</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Diagnósticos</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={maintenanceData.diagnostics}
                                            onChange={(e) => handleMaintenanceDataChange('diagnostics', e.target.value)}
                                            placeholder="Descripción de los diagnósticos realizados..."
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Resultados</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={maintenanceData.results}
                                            onChange={(e) => handleMaintenanceDataChange('results', e.target.value)}
                                            placeholder="Resultados del mantenimiento..."
                                        />
                                    </Form.Group>
                                </Row>
                                <Row>
                                    <Form.Group as={Col} md={12}>
                                        <Form.Label>Notas Adicionales</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={maintenanceData.additional_notes}
                                            onChange={(e) => handleMaintenanceDataChange('additional_notes', e.target.value)}
                                            placeholder="Cualquier información adicional relevante..."
                                        />
                                    </Form.Group>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Summary */}
                        <Card className="mb-3 bg-light">
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <strong>Elementos Seleccionados: </strong>
                                        {Object.keys(selectedChecks).length}
                                    </Col>
                                    <Col md={6} className="text-end">
                                        <strong>Costo Total Estimado: </strong>
                                        ${calculateTotalCost().toFixed(2)}
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="text-end">
                            <Button variant="secondary" onClick={props.onHide} className="me-2">
                                Cancelar
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Guardando...' : 'Guardar Mantenimiento'}
                            </Button>
                        </div>
                    </Form>
                </Container>
            </Modal.Body>
        </Modal>
    );
}

class ModalWithGrid extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = { modalShow: false };
    }

    render() {
        let add_new_maintenance = () => {
            this.props.setData();
            this.setState({ modalShow: false });
        }

        return (
            <ButtonToolbar>
                <Button
                    variant="primary"
                    onClick={() => this.setState({ modalShow: true })}
                >
                    Agregar Mantenimiento
                </Button>
                <DefaultModal
                    show={this.state.modalShow}
                    onHide={add_new_maintenance}
                    setData={this.props.setData}
                    inventoryOptions={this.props.inventoryOptions}
                    maintenanceAreas={this.props.maintenanceAreas}
                />
            </ButtonToolbar>
        );
    }
}

export default ModalWithGrid;
